import React, { useState, useRef, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import { FiCalendar, FiX } from "react-icons/fi";

const DateRangePicker = ({ value, onChange, showShortcuts = false, classNames = "", popupAlign = "right" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);
  const inputRef = useRef(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [activeShortcut, setActiveShortcut] = useState(null);

  const shortcuts = ["Today", "Yesterday", "Last 7 days", "Last 30 days", "This month", "Last month"];

  // Function to get date range for a shortcut
  const getShortcutDateRange = (shortcut) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start, end;

    switch (shortcut) {
      case "Today":
        start = new Date(today);
        end = new Date(today);
        break;
      case "Yesterday":
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case "Last 7 days":
        // startDate = today - 6 days, endDate = today (7 days total including today)
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        end = new Date(today);
        break;
      case "Last 30 days":
        // startDate = today - 29 days, endDate = today (30 days total including today)
        start = new Date(today);
        start.setDate(start.getDate() - 29);
        end = new Date(today);
        break;
      case "This month":
        // startDate = first day of current month, endDate = today
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        break;
      case "Last month":
        // startDate = first day of previous month, endDate = last day of previous month
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        end.setHours(0, 0, 0, 0);
        break;
      default:
        return null;
    }

    // Normalize all dates to start of day for consistent comparison and display
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return { start, end };
  };

  // Function to check if two dates match (ignoring time)
  const datesMatch = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Function to detect which shortcut matches the current date range
  const detectMatchingShortcut = (startDate, endDate) => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    for (const shortcut of shortcuts) {
      const range = getShortcutDateRange(shortcut);
      if (range && datesMatch(start, range.start) && datesMatch(end, range.end)) {
        return shortcut;
      }
    }
    return null;
  };

  // Initialize from value prop
  useEffect(() => {
    if (value?.startDate && value?.endDate) {
      // Normalize dates to start of day for consistent display and highlighting
      const start = new Date(value.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(value.endDate);
      end.setHours(0, 0, 0, 0);
      setDateRange([start, end]);
      // Detect which shortcut matches the current date range
      const matchingShortcut = detectMatchingShortcut(start, end);
      if (matchingShortcut) {
        setActiveShortcut(matchingShortcut);
      } else {
        // If no shortcut matches, clear active shortcut (manual date selection)
        setActiveShortcut(null);
      }
    } else if (value?.startDate) {
      const start = new Date(value.startDate);
      start.setHours(0, 0, 0, 0);
      setDateRange([start, null]);
      setActiveShortcut(null);
    } else {
      setDateRange([null, null]);
      // Only reset active shortcut when dates are cleared
      setActiveShortcut(null);
    }
  }, [value?.startDate, value?.endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleDateChange = (range) => {
    // Normalize dates to start of day for consistent display and highlighting
    let normalizedRange = [null, null];
    if (range[0]) {
      normalizedRange[0] = new Date(range[0]);
      normalizedRange[0].setHours(0, 0, 0, 0);
    }
    if (range[1]) {
      normalizedRange[1] = new Date(range[1]);
      normalizedRange[1].setHours(0, 0, 0, 0);
    }
    
    setDateRange(normalizedRange);
    if (normalizedRange[0] && normalizedRange[1]) {
      onChange({
        startDate: normalizedRange[0],
        endDate: normalizedRange[1],
      });
      setIsOpen(false);
      // Clear active shortcut when manually selecting dates
      setActiveShortcut(null);
    }
  };

  const handleShortcut = (shortcut) => {
    const range = getShortcutDateRange(shortcut);
    if (!range) return;

    const { start, end } = range;
    // Ensure dates are normalized (already done in getShortcutDateRange, but double-check)
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    setDateRange([start, end]);
    // Set the active shortcut to the one that was clicked BEFORE calling onChange
    // This ensures the highlight persists even after the value prop updates
    setActiveShortcut(shortcut);
    onChange({
      startDate: start,
      endDate: end,
    });
    // Keep popup open briefly to show the highlight, then close it
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const formatDateRange = () => {
    if (!dateRange[0] || !dateRange[1]) {
      return "YYYY-MM-DD ~ YYYY-MM-DD";
    }
    // Ensure dates are normalized before formatting
    const start = new Date(dateRange[0]);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange[1]);
    end.setHours(0, 0, 0, 0);
    return `${moment(start).format("YYYY-MM-DD")} ~ ${moment(end).format("YYYY-MM-DD")}`;
  };

  const handleClear = (e) => {
    e.stopPropagation(); // Prevent opening the calendar
    setDateRange([null, null]);
    onChange({
      startDate: null,
      endDate: null,
    });
    // Clear active shortcut when clearing dates
    setActiveShortcut(null);
  };

  // Function to check if a shortcut is active
  const isShortcutActive = (shortcut) => {
    return activeShortcut === shortcut;
  };

  return (
    <div className={`relative ${classNames}`}>
      <div
        ref={inputRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex cursor-pointer items-center rounded-lg  border-gray-600 bg-navy-800 px-3 py-2 text-sm hover:border-gray-500 date-picker-input"
        style={{ minWidth: '270px', minHeight: '40px' }}
      >
        <input
          type="text"
          readOnly
          value={formatDateRange()}
          placeholder="YYYY-MM-DD ~ YYYY-MM-DD"
          className="w-full bg-navy-800 outline-none text-gray-500 placeholder:text-gray-600"
          style={{ color: '#9CA3AF' }}
        />
        {dateRange[0] && dateRange[1] ? (
          <FiX 
            className="ml-2 h-5 w-5 text-gray-400 hover:text-white cursor-pointer" 
            onClick={handleClear}
            style={{ color: '#FFFFFF' }}
          />
        ) : (
          <FiCalendar className="ml-2 h-5 w-5 text-gray-500" style={{ color: '#FFFFFF' }} />
        )}
      </div>

      {isOpen && (
        <div
          ref={pickerRef}
          className={`absolute left-0 ${popupAlign === "left" ? "md:left-0" : "md:right-0 md:left-auto"} top-full z-50 mt-2 flex rounded-lg border border-gray-600/30 shadow-xl date-picker-popup`}
          style={{ backgroundColor: "#111c44" }}
        >
          {showShortcuts && (
            <div className="border-r border-gray-600/30 date-picker-shortcuts">
              <div className="space-y-1">
                {shortcuts.map((shortcut) => {
                  const isActive = isShortcutActive(shortcut);
                  return (
                    <button
                      key={shortcut}
                      onClick={() => handleShortcut(shortcut)}
                      className={`date-picker-shortcut-btn ${isActive ? 'active' : ''}`}
                    >
                      {shortcut}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="date-picker-calendar-wrapper">
            <Calendar
              onChange={handleDateChange}
              value={dateRange[0] && dateRange[1] ? [
                new Date(dateRange[0].getTime()),
                new Date(dateRange[1].getTime())
              ] : dateRange}
              selectRange={true}
              className="custom-calendar-compact"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
